import React, { FormEvent, useRef, useState } from 'react'
import cx from 'classnames'
import { Icon, l, TextArea } from '@manychat/manyui'

import { ConversationStatus } from 'apps/aiAutomation/components/Playground/types'

import cm from './styles.module.css'

interface MessageFormProps {
  placeholder: string
  ariaLabel: string
  disabled?: boolean

  sessionId: string | null

  conversationStatus: ConversationStatus

  onStartSession?: (message: string) => void
  onSendMessage: (message: string) => void
}

export const MessageForm = ({
  placeholder,
  ariaLabel,
  sessionId,
  conversationStatus,
  onStartSession,
  onSendMessage,
  disabled,
}: MessageFormProps) => {
  const isConversationFinished = conversationStatus === ConversationStatus.FINISHED
  const isOngoingWaitingConversation = conversationStatus === ConversationStatus.ONGOING_WAITING
  const isOngoingConversation = conversationStatus === ConversationStatus.ONGOING
  const isIdleConversation = conversationStatus === ConversationStatus.IDLE

  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  const [message, setMessage] = useState('')

  const submitMessage = () => {
    if (isOngoingWaitingConversation) {
      return
    }

    const trimmedMessage = message.trim()

    if (trimmedMessage.length === 0) {
      return
    }

    if (!sessionId && onStartSession) {
      onStartSession(trimmedMessage)
    } else {
      onSendMessage(trimmedMessage)
    }

    setMessage('')

    inputRef.current?.focus?.()
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    submitMessage()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.metaKey && !event.shiftKey) {
      event.preventDefault()

      submitMessage()
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value)
  }

  return (
    <div>
      <form
        className={cx(cm.form, (isConversationFinished || disabled) && cm.form__disabled)}
        onSubmit={handleSubmit}
      >
        <TextArea
          ref={inputRef}
          className={cm.input}
          value={message}
          disabled={disabled || isConversationFinished}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          minRows={1}
          maxRows={3}
          placeholder={placeholder}
          ariaLabel={ariaLabel}
          variant="default"
          autoFocus
        />

        <button
          className={cm.sendButton}
          onClick={() => submitMessage()}
          aria-label={l.translate('Send message')}
          disabled={disabled || (!isOngoingConversation && !isIdleConversation)}
        >
          <Icon.Send />
        </button>
      </form>

      <p className="m-t-xs text-body-sm text-disabled">
        {l.translate('Manychat AI can make mistakes.')}
      </p>
    </div>
  )
}
